import { useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { v4 as uuid } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useMSRNudgeRecommendations } from '@atlassian/cross-join-nudges';

import { useFireEventOnce } from '../use-fire-event-once/index.tsx';
import { useRunMsrNudges } from '../../services/msr-cross-join-nudge.ts';
import { useFireOnlyNewLoomEvent } from '../use-fire-only-new-loom-event/index.tsx';

export const useCrossJoinNudges = ({ flags }: { flags: FlagsStateContainer }) => {
	const { formatMessage } = useIntl();
	const { cloudId, userId, locale } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticEventOnceForMsr = useFireEventOnce();
	const fireAnalyticEventOnceForLoom = useFireEventOnce();
	const fireOnlyNewLoomEvent = useFireOnlyNewLoomEvent();
	const { push } = useRouteActions();
	const runMsrNudges = useRunMsrNudges({ cloudId, userId, flags, createAnalyticsEvent, push });
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product: 'confluence',
		sessionId: uuid(),
		cloudId,
		subProduct: '',
	});

	useEffect(() => {
		const execNudges = async () => {
			const {
				checkJpdCrossJoinNudgeEligibility,
				showJpdCrossJoinNudge,
				triggerJiraCrossJoinNudge,
			} = await import(
				/* webpackChunkName: "loadable-cross-join-nudges-services" */ '../../services/run-cross-join-nudges.ts'
			);

			const isJpdCrossJoinNudgesEligible = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
				getMSRNudgeRecommendations,
			});

			isJpdCrossJoinNudgesEligible
				? await showJpdCrossJoinNudge({ cloudId, userId, flags, createAnalyticsEvent, push })
				: await triggerJiraCrossJoinNudge({
						cloudId,
						userId,
						flags,
						createAnalyticsEvent,
						push,
						getMSRNudgeRecommendations,
					});
		};

		const execNudgesWithLoom = async () => {
			if (
				!userId ||
				!(locale || '').toLowerCase().startsWith('en') ||
				!fg('twcg-443-cjn-confluence-loom-killswitch')
			) {
				await execNudges();
				return;
			}

			const { checkLoomCrossJoinNudgeEligibility, showLoomCrossJoinNudge } = await import(
				/* webpackChunkName: "loadable-loom-cross-join-nudges-services" */ '../../services/loom-cross-join-nudge.ts'
			);

			const isLoomCrossJoinNudgesEligible = await checkLoomCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
			});

			if (isLoomCrossJoinNudgesEligible.eligible) {
				if (expVal('twcg-443-cjn-confluence-loom', 'cohort', 'not-enrolled') !== 'not-enrolled') {
					fireAnalyticEventOnceForLoom(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'confluenceToLoomNudgeFeature',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-443-cjn-confluence-loom',
									cohort: expVal('twcg-443-cjn-confluence-loom', 'cohort', 'not-enrolled'),
								},
							},
						}),
					);
				}
				await showLoomCrossJoinNudge({
					cloudId,
					userId,
					flags,
					createAnalyticsEvent,
					formatMessage,
				});
			} else {
				// Track eligibility failure reasons
				fireOnlyNewLoomEvent({
					value: isLoomCrossJoinNudgesEligible,
					key: 'cjn-conf-loom',
				});
				await execNudges();
			}
		};

		const orchestrateNudges = async () => {
			const { checkMsrCrossJoinNudgeEligibility, setEnrolledInMsr } = await import(
				/* webpackChunkName: "loadable-msr-cross-join-nudges-services" */ '../../services/msr-cross-join-nudge.ts'
			);

			if (await checkMsrCrossJoinNudgeEligibility()) {
				if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'variation')) {
					fireAnalyticEventOnceForMsr(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'msrCjnExperiment',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-437-cjn-msr-m1',
									value: 'variation',
								},
							},
						}),
					);
					setEnrolledInMsr();
					await runMsrNudges();
				} else if (expValEquals('twcg-437-cjn-msr-m1', 'cohort', 'control')) {
					fireAnalyticEventOnceForMsr(
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'msrCjnExperiment',
								source: 'crossJoinNudges',
								attributes: {
									flagKey: 'twcg-437-cjn-msr-m1',
									value: 'control',
								},
							},
						}),
					);
					setEnrolledInMsr();
					await execNudges();
				} else {
					await execNudges();
				}
			} else {
				await execNudgesWithLoom();
			}
		};

		const init = async () => {
			await orchestrateNudges();
		};

		void init();
		// Disabling as we only want this to execute based on `execNudge` or if `flags` gets updated.
		// This prevents edge cases where flags might be shown twice due to
		// Complex dependency arrays of children components/dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags]);
};

export const CrossJoinNudgesWithFlags = withFlags(({ flags }) => {
	useCrossJoinNudges({ flags });
	return null;
});
